import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Upload, Download, Users, Pencil, Trash2, ArrowLeft, ChevronDown } from 'lucide-react';
import * as api from '../lib/api';
import { formatPrice, formatDateTimeShort, kindOf } from '../lib/eventsFormat';
import { prepareImage } from '../lib/imageResize';

const TIMEZONES = [
  ['America/New_York', 'Eastern'], ['America/Chicago', 'Central'], ['America/Denver', 'Mountain'],
  ['America/Los_Angeles', 'Pacific'], ['America/Toronto', 'Toronto'], ['Europe/London', 'London'],
];

const BLANK = {
  title: '', description: '', event_type: 'in_person', is_ticketed: true, status: 'draft', timezone: 'America/New_York',
  starts_local: '', ends_local: '', city: '', venue_name: '', venue_address: '', venue_partner: '', online_url: '',
  space_id: '', capacity: '', separate_by_gender: false, capacity_brothers: '', capacity_sisters: '', price: '',
  premium_discount_pct: 10, premium_presale_local: '', general_sale_local: '', registration_closes_local: '',
  refund_window_hours: 72,
};

const fromEvent = (ev) => ({
  ...BLANK, ...Object.fromEntries(Object.keys(BLANK).map((k) => [k, ev[k] ?? BLANK[k]])),
  price: ev.price_cents ? (ev.price_cents / 100).toFixed(2) : '',
  capacity: ev.capacity ?? '', capacity_brothers: ev.capacity_brothers ?? '', capacity_sisters: ev.capacity_sisters ?? '',
  starts_local: ev.starts_local || '', ends_local: ev.ends_local || '',
  premium_presale_local: ev.premium_presale_local || '', general_sale_local: ev.general_sale_local || '',
  registration_closes_local: ev.registration_closes_local || '', space_id: ev.space_id || '',
});

const toPayload = (f) => {
  const text = (v) => (v && String(v).trim() ? String(v).trim() : null);
  const int = (v) => (v === '' || v === null || v === undefined ? null : parseInt(v, 10));
  const price = parseFloat(f.price);
  return {
    title: f.title.trim(), description: text(f.description), event_type: f.event_type, is_ticketed: f.is_ticketed,
    status: f.status, timezone: f.timezone, starts_local: text(f.starts_local), ends_local: text(f.ends_local),
    city: text(f.city), venue_name: text(f.venue_name), venue_address: text(f.venue_address),
    venue_partner: text(f.venue_partner), online_url: text(f.online_url), space_id: text(f.space_id),
    capacity: int(f.capacity), separate_by_gender: f.separate_by_gender,
    capacity_brothers: int(f.capacity_brothers), capacity_sisters: int(f.capacity_sisters),
    price_cents: Number.isFinite(price) ? Math.round(price * 100) : 0,
    premium_discount_pct: int(f.premium_discount_pct) ?? 0,
    premium_presale_local: text(f.premium_presale_local), general_sale_local: text(f.general_sale_local),
    registration_closes_local: text(f.registration_closes_local), refund_window_hours: int(f.refund_window_hours) ?? 72,
  };
};

const inputCls = 'w-full min-h-[44px] px-4 py-2.5 bg-white/70 dark:input-white-dark rounded-arch border border-teal/20 focus:ring-2 focus:ring-primary text-text-main';

const Field = ({ id, label, hint, children }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-semibold text-text-main mb-1.5">{label}</label>
    {children}
    {hint && <p className="text-xs text-text-muted mt-1">{hint}</p>}
  </div>
);

const Toggle = ({ id, label, hint, checked, onChange }) => (
  <div className="flex items-center justify-between gap-4 p-4 rounded-arch bg-white/50 dark:bg-white/5">
    <div>
      <label htmlFor={id} className="font-heading font-semibold text-text-main cursor-pointer">{label}</label>
      {hint && <p className="text-xs text-text-muted">{hint}</p>}
    </div>
    <input id={id} type="checkbox" role="switch" checked={checked} onChange={(e) => onChange(e.target.checked)} className="w-6 h-6 accent-[#4AADA8] shrink-0" />
  </div>
);

const Attendees = ({ event, onBack, flash }) => {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => {
    api.adminGetEventRegistrations(event.id).then((r) => setRows(r.data || [])).catch(() => flash('error', 'Could not load attendees'));
  }, [event.id, flash]);
  useEffect(() => { load(); }, [load]);

  const toggleAttended = async (r) => {
    try { await api.adminMarkAttended(r.id, r.status !== 'attended'); load(); }
    catch (e) { flash('error', e?.response?.data?.detail || 'Could not update'); }
  };
  const togglePhotos = async (r) => {
    try { await api.adminSetPhotoOptOut(r.id, !r.photo_opt_out); load(); }
    catch (e) { flash('error', e?.response?.data?.detail || 'Could not update'); }
  };
  const refund = async (r) => {
    const who = r.users?.display_name || 'this member';
    if (!window.confirm(`Refund ${who} in full and free their seat? This cannot be undone.`)) return;
    try { await api.adminRefundRegistration(r.id); flash('success', 'Refunded'); load(); }
    catch (e) { flash('error', e?.response?.data?.detail || 'Refund failed'); }
  };
  const syncKit = async () => {
    try {
      const res = await api.adminSyncKit(event.id);
      flash(res.data.configured ? 'success' : 'error',
        res.data.configured ? `Sending ${res.data.queued} attendee(s) to Kit with the tags "${res.data.tags.join('" and "')}". Check Kit in a minute.` : 'The Kit key is not set on the backend.');
    } catch (e) { flash('error', e?.response?.data?.detail || 'Could not start the Kit sync'); }
  };
  const download = async () => {
    try {
      const res = await api.adminDownloadEventCsv(event.id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${event.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-attendees.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { flash('error', 'Could not export'); }
  };

  const live = (rows || []).filter((r) => ['confirmed', 'attended', 'refund_pending'].includes(r.status));
  return (
    <div data-testid="event-attendees">
      <button onClick={onBack} className="min-h-[44px] inline-flex items-center gap-1 text-primary font-semibold"><ArrowLeft className="w-4 h-4" aria-hidden="true" />All events</button>
      <h2 className="font-heading text-xl font-semibold text-text-main mt-1">{event.title}</h2>
      <p className="text-sm text-text-muted mb-4">{live.length} registered{event.capacity ? ` of ${event.capacity}` : ''}</p>
      <button onClick={download} className="min-h-[44px] px-4 rounded-full border border-teal/30 text-sm font-semibold inline-flex items-center gap-2 hover:bg-teal/10" data-testid="export-attendees-btn">
        <Download className="w-4 h-4" aria-hidden="true" />Export CSV
      </button>
      <button onClick={syncKit} className="min-h-[44px] px-4 ml-2 rounded-full border border-primary/30 text-sm font-semibold inline-flex items-center gap-2 hover:bg-primary/10" data-testid="sync-kit-btn">
        Send to Kit
      </button>
      <div className="overflow-x-auto mt-4">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-text-muted"><th className="py-2 pr-3">Member</th><th className="pr-3">Status</th><th className="pr-3">Paid</th><th className="pr-3">Here</th><th className="pr-3">No photos</th><th /></tr></thead>
          <tbody>
            {rows === null && <tr><td colSpan="6" className="py-4 text-text-muted">Loading...</td></tr>}
            {rows && rows.length === 0 && <tr><td colSpan="6" className="py-4 text-text-muted">No registrations yet.</td></tr>}
            {(rows || []).map((r) => (
              <tr key={r.id} className="border-t border-teal/15">
                <td className="py-2.5 pr-3"><span className="font-medium text-text-main">{r.users?.display_name}</span><br /><span className="text-xs text-text-muted">{r.users?.email}</span></td>
                <td className="pr-3 capitalize">{r.status.replace('_', ' ')}</td>
                <td className="pr-3">${((r.amount_cents || 0) / 100).toFixed(2)}{r.premium_discount_applied ? ' (Sirati+)' : ''}</td>
                <td className="pr-3">
                  {['confirmed', 'attended'].includes(r.status) && (
                    <input type="checkbox" checked={r.status === 'attended'} onChange={() => toggleAttended(r)} aria-label={`Mark ${r.users?.display_name} as attended`} className="w-5 h-5 accent-[#4AADA8]" />
                  )}
                </td>
                <td className="pr-3">
                  {['confirmed', 'attended'].includes(r.status) && (
                    <input type="checkbox" checked={!!r.photo_opt_out} onChange={() => togglePhotos(r)} aria-label={`${r.users?.display_name} does not want to be photographed`} className="w-5 h-5 accent-[#4AADA8]" data-testid={`no-photos-${r.id}`} />
                  )}
                </td>
                <td className="text-right">
                  {['confirmed', 'attended', 'refund_pending', 'pending_payment', 'waitlisted'].includes(r.status) && (
                    <button onClick={() => refund(r)} className="min-h-[44px] px-3 text-red-600 font-semibold hover:underline" data-testid={`refund-${r.id}`}>Refund</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const EventForm = ({ initial, spaces, onSaved, onCancel, flash }) => {
  const editing = !!initial?.id;
  const [f, setF] = useState(initial ? fromEvent(initial) : BLANK);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imageUrl, setImageUrl] = useState(initial?.image_url || null);
  const [focusY, setFocusY] = useState(initial?.image_focus_y ?? 50);
  const [uploadError, setUploadError] = useState('');
  const [uploadNote, setUploadNote] = useState('');
  const set = (k) => (e) => setF((cur) => ({ ...cur, [k]: e.target.value }));
  const setVal = (k, v) => setF((cur) => ({ ...cur, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    if (!f.title.trim() || !f.starts_local) { flash('error', 'A title and start time are required'); return; }
    if (f.status === 'cancelled' && !window.confirm('Cancelling does not refund anyone automatically. Refund attendees from the Attendees view. Continue?')) return;
    setSaving(true);
    try {
      const payload = toPayload(f);
      if (focusY !== (initial?.image_focus_y ?? 50)) payload.image_focus_y = focusY;   // only sent when moved, so saving works before the SQL migration is run
      const res = editing ? await api.adminUpdateEvent(initial.id, payload) : await api.adminCreateEvent(payload);
      flash('success', editing ? 'Event saved' : 'Event created. You can now add a photo.');
      onSaved(res.data, !editing);
    } catch (err) {
      flash('error', err?.response?.data?.detail || 'Could not save the event');
    } finally { setSaving(false); }
  };

  const upload = async (e) => {
    const picked = e.target.files?.[0];
    if (!picked || !initial?.id) return;
    setUploading(true);
    setUploadError('');
    setUploadNote('');
    try {
      const { file, note } = await prepareImage(picked, { maxDimension: 1600 });
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.adminUploadEventImage(initial.id, fd);
      setImageUrl(res.data.url);
      setUploadNote(note || 'Photo updated.');
    } catch (err) {
      setUploadError(err?.response?.data?.detail || err?.message || 'Upload failed. Please try another photo.');
    }
    setUploading(false);
    e.target.value = '';
  };

  return (
    <form onSubmit={save} className="space-y-5" data-testid="event-form">
      <button type="button" onClick={onCancel} className="min-h-[44px] inline-flex items-center gap-1 text-primary font-semibold"><ArrowLeft className="w-4 h-4" aria-hidden="true" />All events</button>
      <h2 className="font-heading text-xl font-semibold text-text-main">{editing ? 'Edit event' : 'New event'}</h2>

      <Field id="ev-title" label="Title"><input id="ev-title" className={inputCls} value={f.title} onChange={set('title')} maxLength={120} required /></Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field id="ev-type" label="Type">
          <select id="ev-type" className={inputCls} value={f.event_type} onChange={(e) => { setVal('event_type', e.target.value); if (e.target.value === 'course_launch') setVal('is_ticketed', false); }}>
            <option value="in_person">In-person event</option><option value="online">Online event</option><option value="course_launch">Course launch</option>
          </select>
        </Field>
        <Field id="ev-status" label="Status" hint="Draft is only visible to admins.">
          <select id="ev-status" className={inputCls} value={f.status} onChange={set('status')}>
            <option value="draft">Draft</option><option value="published">Published</option><option value="cancelled">Cancelled</option>
          </select>
        </Field>
      </div>
      <Field id="ev-desc" label="Description" hint="Put **two asterisks** around words to make them bold."><textarea id="ev-desc" className={`${inputCls} min-h-[110px]`} value={f.description || ''} onChange={set('description')} maxLength={5000} /></Field>

      <div>
        <p className="text-sm font-semibold text-text-main mb-1.5">Photo</p>
        {editing ? (
          <>
          <div className="flex items-center gap-4 flex-wrap">
            {imageUrl && <img src={imageUrl} alt="" className="w-32 h-20 object-cover rounded-arch" style={{ objectPosition: `50% ${focusY}%` }} />}
            <label className="min-h-[44px] px-4 rounded-full border border-teal/30 text-sm font-semibold inline-flex items-center gap-2 cursor-pointer hover:bg-teal/10 focus-within:ring-2 focus-within:ring-primary">
              <Upload className="w-4 h-4" aria-hidden="true" />{uploading ? 'Uploading...' : imageUrl ? 'Change photo' : 'Add photo'}
              <input type="file" accept="image/*,.heic,.heif" onChange={upload} disabled={uploading} className="sr-only" aria-describedby="ev-photo-help" />
            </label>
          </div>
          <p id="ev-photo-help" className="text-xs text-text-muted mt-1.5">Any photo works. It is resized for you (about 1600 px wide is ideal). Keep faces and text near the middle, because the edges can be cropped.</p>
          {imageUrl && (
            <div className="mt-3 max-w-md">
              <label htmlFor="ev-focus" className="text-sm font-semibold text-text-main">Photo position</label>
              <input id="ev-focus" type="range" min="0" max="100" value={focusY} onChange={(e) => setFocusY(parseInt(e.target.value, 10))} className="w-full min-h-[44px] accent-teal" aria-valuetext={`${focusY} percent from the top`} />
              <div className="flex justify-between text-xs text-text-muted"><span>Show top</span><span>Show bottom</span></div>
              <div className="relative h-[120px] mt-2 rounded-arch overflow-hidden"><img src={imageUrl} alt="" className="w-full h-full object-cover" style={{ objectPosition: `50% ${focusY}%` }} /></div>
              <p className="text-xs text-text-muted mt-1">Preview of the crop on the event page. Save to keep it.</p>
            </div>
          )}
          {uploadError && <p role="alert" className="text-sm text-red-600 mt-2" data-testid="photo-error">{uploadError}</p>}
          {uploadNote && !uploadError && <p role="status" className="text-sm text-green-700 mt-2" data-testid="photo-note">{uploadNote}</p>}
          </>
        ) : <p className="text-sm text-text-muted">Save the event first, then add a photo.</p>}
      </div>

      <h3 className="font-heading font-semibold text-primary pt-2">When and where</h3>
      <div className="grid sm:grid-cols-3 gap-4">
        <Field id="ev-tz" label="Timezone"><select id="ev-tz" className={inputCls} value={f.timezone} onChange={set('timezone')}>{TIMEZONES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Field>
        <Field id="ev-start" label="Starts"><input id="ev-start" type="datetime-local" className={inputCls} value={f.starts_local} onChange={set('starts_local')} required /></Field>
        <Field id="ev-end" label="Ends"><input id="ev-end" type="datetime-local" className={inputCls} value={f.ends_local} onChange={set('ends_local')} /></Field>
      </div>
      {f.event_type !== 'online' && f.event_type !== 'course_launch' && (
        <div className="grid sm:grid-cols-2 gap-4">
          <Field id="ev-city" label="City, State, Country (public)" hint="Spell it out for a global community, e.g. Glen Allen, Virginia, United States."><input id="ev-city" className={inputCls} value={f.city || ''} onChange={set('city')} placeholder="Glen Allen, Virginia, United States" /></Field>
          <Field id="ev-partner" label="Venue partner (public, optional)"><input id="ev-partner" className={inputCls} value={f.venue_partner || ''} onChange={set('venue_partner')} /></Field>
          <Field id="ev-venue" label="Venue name (hidden until registered)"><input id="ev-venue" className={inputCls} value={f.venue_name || ''} onChange={set('venue_name')} /></Field>
          <Field id="ev-addr" label="Address (hidden until registered)"><input id="ev-addr" className={inputCls} value={f.venue_address || ''} onChange={set('venue_address')} /></Field>
        </div>
      )}
      {f.event_type === 'online' && (
        <Field id="ev-url" label="Online link (hidden until registered)" hint="Must start with https://"><input id="ev-url" className={inputCls} value={f.online_url || ''} onChange={set('online_url')} /></Field>
      )}
      {f.event_type === 'course_launch' && (
        <Field id="ev-space" label="Course (space)">
          <select id="ev-space" className={inputCls} value={f.space_id} onChange={set('space_id')}>
            <option value="">None</option>{spaces.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </Field>
      )}

      <Toggle id="ev-ticketed" label="Ticketed" hint="Turn off for course launches and free sessions that need no registration." checked={f.is_ticketed} onChange={(v) => setVal('is_ticketed', v)} />

      {f.is_ticketed && (
        <>
          <h3 className="font-heading font-semibold text-primary pt-2">Tickets and capacity</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field id="ev-price" label="Price (USD)" hint="Leave blank for a free ticket."><input id="ev-price" inputMode="decimal" className={inputCls} value={f.price} onChange={set('price')} placeholder="35.00" /></Field>
            <Field id="ev-disc" label="Sirati+ discount (%)"><input id="ev-disc" inputMode="numeric" className={inputCls} value={f.premium_discount_pct} onChange={set('premium_discount_pct')} /></Field>
            <Field id="ev-cap" label="Capacity" hint="Leave blank for unlimited."><input id="ev-cap" inputMode="numeric" className={inputCls} value={f.capacity} onChange={set('capacity')} /></Field>
            <Field id="ev-refund" label="Full refund until (hours before)"><input id="ev-refund" inputMode="numeric" className={inputCls} value={f.refund_window_hours} onChange={set('refund_window_hours')} /></Field>
            <Field id="ev-presale" label="Sirati+ early access starts" hint="Optional. If set, also set when everyone else can buy."><input id="ev-presale" type="datetime-local" className={inputCls} value={f.premium_presale_local} onChange={set('premium_presale_local')} /></Field>
            <Field id="ev-general" label="Everyone else can buy from" hint="Blank means on sale as soon as it is published."><input id="ev-general" type="datetime-local" className={inputCls} value={f.general_sale_local} onChange={set('general_sale_local')} /></Field>
            <Field id="ev-close" label="Registration closes"><input id="ev-close" type="datetime-local" className={inputCls} value={f.registration_closes_local} onChange={set('registration_closes_local')} /></Field>
          </div>
          <Toggle id="ev-gender" label="Separate by gender" hint="Off by default. Turn on to cap brothers and sisters separately." checked={f.separate_by_gender} onChange={(v) => setVal('separate_by_gender', v)} />
          {f.separate_by_gender && (
            <div className="grid sm:grid-cols-2 gap-4">
              <Field id="ev-capb" label="Brothers capacity"><input id="ev-capb" inputMode="numeric" className={inputCls} value={f.capacity_brothers} onChange={set('capacity_brothers')} /></Field>
              <Field id="ev-caps" label="Sisters capacity"><input id="ev-caps" inputMode="numeric" className={inputCls} value={f.capacity_sisters} onChange={set('capacity_sisters')} /></Field>
            </div>
          )}
        </>
      )}

      <div className="flex gap-3 flex-wrap pt-2">
        <button type="submit" disabled={saving} className="min-h-[48px] px-7 rounded-full bg-primary text-white font-bold btn-hover disabled:opacity-50" data-testid="save-event-btn">{saving ? 'Saving...' : 'Save event'}</button>
        <button type="button" onClick={onCancel} className="min-h-[48px] px-6 rounded-full border-2 border-teal/40 text-primary font-bold hover:bg-teal/10">Cancel</button>
      </div>
    </form>
  );
};

const KitStatus = () => {
  const [result, setResult] = useState(null);
  const [checking, setChecking] = useState(false);
  const check = async () => {
    setChecking(true);
    try { setResult((await api.adminKitCheck()).data); }
    catch { setResult({ ok: false, detail: 'Could not run the check.' }); }
    setChecking(false);
  };
  return (
    <div className="flex items-center justify-between gap-4 p-4 mb-5 rounded-arch border border-white/20 bg-white/40 dark:bg-white/10" data-testid="kit-status-card">
      <div className="min-w-0">
        <p className="font-heading font-semibold text-text-main">Kit email connection</p>
        <p className={`text-xs ${result ? (result.ok ? 'text-green-700' : 'text-red-600') : 'text-text-muted'}`} role="status">
          {result ? `${result.ok ? 'Connected' : 'Not working'}${result.status ? ` (${result.status})` : ''}: ${result.detail}` : 'Registrants are tagged in Kit when they register.'}
        </p>
      </div>
      <button onClick={check} disabled={checking} className="min-h-[44px] px-4 rounded-full border border-primary/30 text-sm font-semibold hover:bg-primary/10 shrink-0 disabled:opacity-50" data-testid="kit-check-btn">
        {checking ? 'Checking...' : 'Test connection'}
      </button>
    </div>
  );
};

const EventsAdmin = () => {
  const [events, setEvents] = useState(null);
  const [spaces, setSpaces] = useState([]);
  const [mode, setMode] = useState({ view: 'list' });
  const [enabled, setEnabled] = useState(null);
  const [msg, setMsg] = useState(null);
  const [showCancelled, setShowCancelled] = useState(false);
  const flash = useCallback((type, text) => setMsg({ type, text }), []);

  const load = useCallback(() => {
    api.adminGetEvents().then((r) => setEvents(r.data || [])).catch(() => flash('error', 'Could not load events'));
  }, [flash]);

  useEffect(() => {
    load();
    api.getGroups().then((r) => setSpaces(r.data || [])).catch(() => {});
    api.getEventsSetting().then((r) => setEnabled(!!r.data?.enabled)).catch(() => setEnabled(false));
  }, [load]);

  const toggleEnabled = async () => {
    try {
      const res = await api.setAdminSetting('events_enabled', !enabled);
      setEnabled(res.data?.value === true || res.data?.value === 'true');
    } catch { flash('error', 'Could not change the setting'); }
  };

  const remove = async (ev) => {
    if (!window.confirm(`Delete "${ev.title}"? This cannot be undone.`)) return;
    try { await api.adminDeleteEvent(ev.id); flash('success', 'Deleted'); load(); }
    catch (e) { flash('error', e?.response?.data?.detail || 'Could not delete'); }
  };

  const active = (events || []).filter((e) => e.status !== 'cancelled');
  const cancelled = (events || []).filter((e) => e.status === 'cancelled');

  const renderRow = (ev) => {
              const kind = kindOf(ev);
              return (
                <li key={ev.id} className={`p-4 rounded-arch bg-white/50 dark:bg-white/5 border border-teal/15 flex items-center gap-4 flex-wrap ${ev.status === 'cancelled' ? 'opacity-75' : ''}`} data-testid={`admin-event-${ev.id}`}>
                  <span className={`w-11 h-11 rounded-2xl text-white flex items-center justify-center shrink-0 ${kind.tile}`}><kind.Icon className="w-5 h-5" aria-hidden="true" /></span>
                  <div className="min-w-0 flex-1">
                    <p className="font-heading font-semibold text-text-main">{ev.title}</p>
                    <p className="text-[13px] text-text-muted">
                      {formatDateTimeShort(ev.starts_at, ev.timezone)} &middot; <span className="capitalize">{ev.status}</span>
                      {ev.is_ticketed && <> &middot; {formatPrice(ev.price_cents)} &middot; {ev.taken?.total || 0}{ev.capacity ? ` / ${ev.capacity}` : ''} taken</>}
                    </p>
                  </div>
                  <div className="flex gap-1 flex-wrap">
                    {ev.is_ticketed && (
                      <button onClick={() => setMode({ view: 'attendees', event: ev })} className="min-h-[44px] px-3 rounded-full text-sm font-semibold text-primary hover:bg-teal/10 inline-flex items-center gap-1.5" data-testid={`attendees-${ev.id}`}>
                        <Users className="w-4 h-4" aria-hidden="true" />Attendees
                      </button>
                    )}
                    <button onClick={() => setMode({ view: 'form', event: ev })} className="min-h-[44px] px-3 rounded-full text-sm font-semibold text-primary hover:bg-teal/10 inline-flex items-center gap-1.5" data-testid={`edit-${ev.id}`}>
                      <Pencil className="w-4 h-4" aria-hidden="true" />Edit
                    </button>
                    <button onClick={() => remove(ev)} aria-label={`Delete ${ev.title}`} className="min-h-[44px] min-w-[44px] rounded-full text-red-600 hover:bg-red-50 inline-flex items-center justify-center">
                      <Trash2 className="w-4 h-4" aria-hidden="true" />
                    </button>
                  </div>
                </li>
              );
  };

  return (
    <div data-testid="events-admin">
      {msg && (
        <div role="status" className={`mb-4 px-4 py-3 rounded-arch flex items-center justify-between ${msg.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          {msg.text}<button onClick={() => setMsg(null)} aria-label="Dismiss message" className="min-h-[44px] min-w-[44px] text-lg leading-none">&times;</button>
        </div>
      )}

      {mode.view === 'list' && (
        <>
          <div className="flex items-center justify-between gap-4 p-4 mb-5 rounded-arch border border-white/20 bg-white/40 dark:bg-white/10" data-testid="events-switch-card">
            <div>
              <p className="font-heading font-semibold text-text-main">Events are {enabled ? 'visible to members' : 'hidden from members'}</p>
              <p className="text-xs text-text-muted">While hidden, only admins see the calendar, event pages and sidebar card.</p>
            </div>
            <button
              role="switch" aria-checked={enabled === true} aria-label="Show events to members" disabled={enabled === null}
              onClick={toggleEnabled} data-testid="events-switch"
              className={`w-14 h-8 rounded-full relative transition-colors shrink-0 ${enabled ? 'bg-primary' : 'bg-olive/30'}`}
            >
              <span className={`absolute top-1 w-6 h-6 rounded-full bg-white transition-all ${enabled ? 'left-7' : 'left-1'}`} />
            </button>
          </div>
          <KitStatus />
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-semibold text-text-main">Events</h2>
            <button onClick={() => setMode({ view: 'form', event: null })} className="min-h-[44px] px-5 rounded-full bg-primary text-white font-bold inline-flex items-center gap-2 btn-hover" data-testid="new-event-btn">
              <Plus className="w-4 h-4" aria-hidden="true" />New event
            </button>
          </div>
          {events === null && <p className="text-text-muted">Loading...</p>}
          {events && events.length === 0 && <p className="text-text-muted">No events yet. Create the first one.</p>}
          {events && events.length > 0 && active.length === 0 && <p className="text-text-muted">No active events. Create one, or open the cancelled list below.</p>}
          <ul className="space-y-3">{active.map(renderRow)}</ul>
          {cancelled.length > 0 && (
            <div className="mt-6" data-testid="cancelled-events">
              <button
                onClick={() => setShowCancelled((v) => !v)}
                aria-expanded={showCancelled} aria-controls="cancelled-events-list"
                className="min-h-[44px] inline-flex items-center gap-2 text-sm font-semibold text-text-muted hover:text-text-main"
                data-testid="cancelled-toggle"
              >
                <ChevronDown className={`w-4 h-4 transition-transform ${showCancelled ? 'rotate-180' : ''}`} aria-hidden="true" />
                Cancelled ({cancelled.length})
              </button>
              {showCancelled && <ul id="cancelled-events-list" className="space-y-3 mt-2">{cancelled.map(renderRow)}</ul>}
            </div>
          )}
        </>
      )}

      {mode.view === 'form' && (
        <EventForm
          key={mode.event?.id || 'new'} initial={mode.event} spaces={spaces} flash={flash}
          onCancel={() => { setMode({ view: 'list' }); load(); }}
          onSaved={(saved, wasNew) => { load(); setMode(wasNew ? { view: 'form', event: saved } : { view: 'list' }); }}
        />
      )}
      {mode.view === 'attendees' && <Attendees event={mode.event} flash={flash} onBack={() => { setMode({ view: 'list' }); load(); }} />}
    </div>
  );
};

export default EventsAdmin;
